import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import LoginForm from './LoginForm';
import config from '../../config';
import './AuthStyles.css';

// Icône Claude AI officielle
const ClaudeIcon = () => (
  <img src="/claude-logo.png" alt="Claude" width={32} height={32} style={{ borderRadius: 8 }} />
);

// Icône Google SVG
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

/**
 * McpAuth - Page d'authentification pour les connecteurs MCP (Claude Desktop)
 *
 * Flow:
 * 1. Claude redirige vers /mcp-auth?mcp_callback=...&scope=...
 * 2. Si l'utilisateur est connecté, on redirige vers le callback avec le token
 * 3. Sinon, on affiche le formulaire de login
 * 4. Après login réussi, on redirige vers le callback avec le token
 */
const McpAuth = ({ mcpCallback, scope }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  // Vérifier si l'utilisateur est déjà connecté ou vient de se connecter via Google
  useEffect(() => {
    const checkAuth = async () => {
      // Vérifier si on a un token dans l'URL (retour de Google OAuth)
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');

      if (urlToken) {
        // Stocker le token et nettoyer l'URL
        localStorage.setItem('kokpeet_token', urlToken);
        // Nettoyer l'URL sans recharger la page
        const cleanUrl = window.location.pathname + '?mcp_callback=' + encodeURIComponent(mcpCallback);
        window.history.replaceState({}, '', cleanUrl);
      }

      const token = localStorage.getItem('kokpeet_token');

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Vérifier que le token est valide
        const response = await fetch(`${config.API_URL}/api/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setIsAuthenticated(true);

          // Si on vient de Google OAuth, rediriger automatiquement
          if (urlToken && mcpCallback) {
            setTimeout(() => {
              handleRedirectToMcp();
            }, 500);
          }
        } else {
          // Token invalide, supprimer
          localStorage.removeItem('kokpeet_token');
        }
      } catch (err) {
        console.error('Erreur vérification auth:', err);
        localStorage.removeItem('kokpeet_token');
      }

      setLoading(false);
    };

    checkAuth();
  }, [mcpCallback]);

  // Rediriger vers le callback MCP avec le token
  const handleRedirectToMcp = () => {
    const token = localStorage.getItem('kokpeet_token');

    if (!token || !mcpCallback) {
      setError('Token ou callback manquant');
      return;
    }

    setRedirecting(true);

    // Construire l'URL de callback avec le token
    const callbackUrl = new URL(mcpCallback);
    callbackUrl.searchParams.set('token', token);

    // Rediriger vers le serveur MCP
    window.location.href = callbackUrl.toString();
  };

  // Après login réussi
  const handleLoginSuccess = (userData, token) => {
    setUser(userData);
    setIsAuthenticated(true);

    // Rediriger automatiquement après login
    setTimeout(() => {
      handleRedirectToMcp();
    }, 500);
  };

  // Connexion via Google OAuth
  const handleGoogleLogin = () => {
    setLoading(true);
    // Rediriger vers Google Auth avec le mcp_callback en paramètre
    window.location.href = `${config.API_URL}/api/auth/google?mcp_callback=${encodeURIComponent(mcpCallback)}`;
  };

  // Affichage pendant le chargement
  if (loading) {
    return (
      <div className="mcp-auth-container">
        <div className="mcp-auth-card">
          <div className="mcp-auth-loading">
            <Loader2 className="spinner" size={40} />
            <p>Vérification de votre session...</p>
          </div>
        </div>
      </div>
    );
  }

  // Affichage pendant la redirection
  if (redirecting) {
    return (
      <div className="mcp-auth-container">
        <div className="mcp-auth-card">
          <div className="mcp-auth-loading">
            <Loader2 className="spinner" size={40} />
            <p>Connexion à Claude en cours...</p>
          </div>
        </div>
      </div>
    );
  }

  // Erreur
  if (error) {
    return (
      <div className="mcp-auth-container">
        <div className="mcp-auth-card">
          <div className="mcp-auth-error">
            <AlertCircle size={48} className="error-icon" />
            <h2>Erreur</h2>
            <p>{error}</p>
            <button
              className="btn btn-primary"
              onClick={() => window.location.href = '/'}
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Si pas de callback, erreur
  if (!mcpCallback) {
    return (
      <div className="mcp-auth-container">
        <div className="mcp-auth-card">
          <div className="mcp-auth-error">
            <AlertCircle size={48} className="error-icon" />
            <h2>Lien invalide</h2>
            <p>Ce lien d'autorisation est invalide ou a expiré.</p>
            <button
              className="btn btn-primary"
              onClick={() => window.location.href = '/'}
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mcp-auth-container">
      <div className="mcp-auth-card">
        {/* Header avec logo Claude */}
        <div className="mcp-auth-header">
          <div className="mcp-auth-logos">
            <div className="mcp-logo claude">
              <ClaudeIcon />
            </div>
            <div className="mcp-arrow">→</div>
            <div className="mcp-logo kokpeet">
              <img src="/logo192.png" alt="Kokpit" width={32} height={32} />
            </div>
          </div>
          <h1>Connecter Claude à Kokpit</h1>
          <p className="mcp-auth-description">
            Claude souhaite accéder à vos données publicitaires Kokpit.
          </p>
        </div>

        {/* Permissions demandées */}
        <div className="mcp-auth-permissions">
          <h3><Shield size={18} /> Autorisations demandées</h3>
          <ul>
            <li>
              <CheckCircle size={16} className="check-icon" />
              Lire vos comptes Google Ads, Meta Ads et Microsoft Ads
            </li>
            <li>
              <CheckCircle size={16} className="check-icon" />
              Exécuter des analyses de performance
            </li>
            <li>
              <CheckCircle size={16} className="check-icon" />
              Lancer des diagnostics de compte
            </li>
          </ul>
        </div>

        {/* Si connecté, bouton d'autorisation */}
        {isAuthenticated ? (
          <div className="mcp-auth-connected">
            <div className="connected-user">
              <CheckCircle size={20} className="success-icon" />
              <span>Connecté en tant que <strong>{user?.email}</strong></span>
            </div>
            <button
              className="btn btn-primary btn-large"
              onClick={handleRedirectToMcp}
            >
              Autoriser l'accès
            </button>
            <button
              className="btn btn-secondary btn-small"
              onClick={() => {
                localStorage.removeItem('kokpeet_token');
                setIsAuthenticated(false);
                setUser(null);
              }}
            >
              Utiliser un autre compte
            </button>
          </div>
        ) : (
          /* Formulaire de connexion */
          <div className="mcp-auth-login">
            <p className="login-prompt">Connectez-vous pour autoriser l'accès :</p>

            {/* Bouton Google OAuth */}
            <button
              onClick={handleGoogleLogin}
              className="google-login-btn"
              style={{ width: '100%', marginBottom: '16px' }}
            >
              <GoogleIcon />
              Continuer avec Google
            </button>

            {/* Séparateur */}
            <div className="auth-divider" style={{ margin: '16px 0' }}>
              <span>ou</span>
            </div>

            <LoginForm
              onSuccess={handleLoginSuccess}
              onSwitchToRegister={() => {
                // Ouvrir l'inscription dans un nouvel onglet
                window.open('/#register', '_blank');
              }}
            />
          </div>
        )}

        {/* Footer */}
        <div className="mcp-auth-footer">
          <p>
            En autorisant l'accès, vous acceptez que Claude puisse accéder à vos données
            publicitaires via l'API Kokpit. Vous pouvez révoquer cet accès à tout moment.
          </p>
        </div>
      </div>
    </div>
  );
};

export default McpAuth;
